/**
 * design_handoff_auth §3/§3b — log in form. Provider button first, then the
 * `or` rule, then the email form (FIXES.md #16). Account-switch CTA is a
 * full-width secondary button under a rule, not a text link (FIXES.md #12).
 *
 * Password + submit sit in a CSS grid (see .passwordGrid in
 * authForm.module.scss) so DOM/tab order — email, password, Log in,
 * Forgot password, Create a free account — can differ from visual order
 * (Forgot password sits top-right of the label, visually before the
 * field). Flexbox `order` was considered and rejected: it doesn't move
 * focus order, only paint order, which is exactly the bug being fixed here.
 *
 * Created by piotr.pozniak@thebeaverhead.com on 23/11/2022
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import classNames from "classnames";
import { Input, Button } from "../../theme/untitled";
import GoogleButton from "../AuthPages/GoogleButton";
import AuthAlert from "../AuthPages/AuthAlert";
import { useUserStore } from "./../../../../hooks/redux/user";
import { useAppStore } from "./../../../../hooks/redux/app";
import useAuthHook from "./../../hooks/useAuthHook";
import useRedirectUrl from "../../hooks/useRedirectUrl";
import { formatError } from "../../../../helpers/errors";
import styles from "../AuthPages/authForm.module.scss";

const WRONG_CREDENTIALS_MESSAGE =
  "That email and password don't match. Check both, or reset your password.";

const LoginEmailForm = () => {
  const [state, setState] = useState({
    email: "",
    password: "",
  });
  const { isLoading: authLoading, authError, onGoogleSignUp } = useAuthHook();

  const { user, login } = useUserStore();
  const { clearErrors } = useAppStore();
  const history = useHistory();
  const { redirectOrNavigateTo, redirectUrl, createUrlWithRedirect } = useRedirectUrl();

  useEffect(() => {
    return clearErrors;
  }, []);

  useEffect(() => {
    if (user.loginSuccess) {
      redirectOrNavigateTo("/dashboard");
    }
  }, [user.loginSuccess]);

  const isWrongCredentials = user.loginError?.response?.status === 401;
  const genericError = isWrongCredentials ? null : user.loginError || authError;
  const hasAnyError = isWrongCredentials || Boolean(genericError);

  const genericErrorMessage = useMemo(() => {
    if (!genericError) return null;
    return formatError(genericError, "Could not log in. Please try again.").join(" ");
  }, [genericError]);

  const onLoginClick = useCallback(
    async (e) => {
      e.preventDefault();
      await login(state.email, state.password);
      return false;
    },
    [state, login]
  );

  const onSetInputValue = useCallback(
    (field) => (e) => {
      setState((prev) => ({ ...prev, [field]: e.target.value }));
      if (hasAnyError) {
        clearErrors();
      }
    },
    [hasAnyError, clearErrors]
  );

  const onSignupWithGoogleClick = useCallback(
    (e) => {
      let flowParam = "login";
      if (redirectUrl) {
        flowParam += "&redirect=" + btoa(encodeURIComponent(redirectUrl));
      }
      onGoogleSignUp(flowParam)(e);
    },
    [redirectUrl]
  );

  const onCreateAccountClick = useCallback(() => {
    history.push(createUrlWithRedirect("/register"));
  }, [history, createUrlWithRedirect]);

  const isLoading = user.login || authLoading;

  const alertRow = genericErrorMessage ? (
    <AuthAlert variant="error" className={styles.passwordGridAlert}>
      {genericErrorMessage}
    </AuthAlert>
  ) : null;

  return (
    <form action="/" noValidate>
      <h1 className={styles.title}>Log in</h1>
      <p className={styles.sub}>
        Welcome back. Your widgets kept syncing while you were away.
      </p>

      <div className={styles.fields}>
        <GoogleButton
          label="Sign in with Google"
          disabled={isLoading}
          onClick={onSignupWithGoogleClick}
        />

        <div className={styles.divider}>or</div>

        <Input
          autoFocus
          label="Email"
          type="email"
          value={state.email}
          onChange={onSetInputValue("email")}
          disabled={isLoading}
          error={isWrongCredentials ? " " : undefined}
        />

        <div className={styles.passwordGrid}>
          <label
            htmlFor="login-password"
            className={classNames(styles.fieldLabel, styles.passwordGridLabel)}
          >
            Password
          </label>

          <Input
            id="login-password"
            className={styles.passwordGridInput}
            type="password"
            value={state.password}
            onChange={onSetInputValue("password")}
            disabled={isLoading}
            error={isWrongCredentials ? WRONG_CREDENTIALS_MESSAGE : undefined}
          />

          {alertRow}

          <Button
            type="submit"
            fullWidth
            loading={isLoading}
            onClick={onLoginClick}
            className={styles.passwordGridButton}
          >
            Log in
          </Button>

          <Link
            className={classNames(styles.inlineLink, styles.passwordGridForgot)}
            to="/password-reminder"
          >
            Forgot password
          </Link>
        </div>
      </div>

      <div className={styles.switchBlock}>
        <p className={styles.switchQuestion}>Don't have an account?</p>
        <Button hierarchy="secondaryGray" fullWidth onClick={onCreateAccountClick}>
          Create a free account
        </Button>
      </div>
    </form>
  );
};

export default LoginEmailForm;
